import { useState } from 'react';
import AnimatedSection from '../../components/AnimatedSection/AnimatedSection';
import styles from './Mentorship.module.css';

const mentors = [
  {
    id: 1,
    name: 'Dr. Sarah Chen',
    initials: 'SC',
    title: 'Neuroscientist, Stanford University',
    field: 'Neuroscience',
    bio: 'Researching neural plasticity and brain resilience after injury. Passionate about making neuroscience accessible to young scientists and mentoring the next generation of researchers.',
    topics: ['Neural plasticity', 'Brain imaging', 'Research methodology'],
    color: '#52D68A',
    available: true,
  },
  {
    id: 2,
    name: 'Prof. Omar Hassan',
    initials: 'OH',
    title: 'Environmental Scientist, MIT',
    field: 'Environmental Science',
    bio: 'Working on climate adaptation strategies and soil carbon sequestration. Has mentored over 30 young researchers and believes open science is the future of climate work.',
    topics: ['Climate science', 'Ecosystem research', 'Data analysis'],
    color: '#38BDF8',
    available: true,
  },
  {
    id: 3,
    name: 'Dr. Maya Patel',
    initials: 'MP',
    title: 'Computational Biologist, Oxford',
    field: 'Computational Biology',
    bio: 'Building AI models to accelerate drug discovery. Passionate about helping students who sit at the intersection of biology and computer science find their path.',
    topics: ['Machine learning', 'Drug discovery', 'Bioinformatics'],
    color: '#A78BFA',
    available: true,
  },
  {
    id: 4,
    name: 'Prof. Luca Rossi',
    initials: 'LR',
    title: 'Particle Physicist, CERN',
    field: 'Physics',
    bio: 'Works on detector development at the Large Hadron Collider. Wants to help young physicists navigate early career research and demystify what working at a major lab looks like.',
    topics: ['Particle physics', 'Detector design', 'Scientific writing'],
    color: '#F59E0B',
    available: false,
  },
  {
    id: 5,
    name: 'Dr. Amara Okonkwo',
    initials: 'AO',
    title: 'Green Chemist, Johns Hopkins',
    field: 'Chemistry',
    bio: 'Developing biodegradable materials from marine algae. A strong advocate for accessible science education and mentoring students from underrepresented backgrounds.',
    topics: ['Green chemistry', 'Biopolymers', 'Sustainable materials'],
    color: '#52D68A',
    available: true,
  },
  {
    id: 6,
    name: 'Dr. Hiroshi Tanaka',
    initials: 'HT',
    title: 'Astronomer, Subaru Telescope',
    field: 'Astronomy',
    bio: 'Studies exoplanet atmospheres and the search for biosignatures. Has mentored young astronomers through citizen science programs and loves connecting curious minds to the cosmos.',
    topics: ['Exoplanets', 'Spectroscopy', 'Citizen science'],
    color: '#38BDF8',
    available: true,
  },
];

const whyMentor = [
  {
    title: 'Shape the next generation',
    description: 'Your experience — the failed experiments, the breakthroughs, the lessons nobody taught you — is exactly what young scientists need to hear.',
    icon: '🌱',
  },
  {
    title: 'Stay inspired',
    description: 'Mentoring young researchers consistently reminds experienced scientists why they fell in love with science in the first place.',
    icon: '✨',
  },
  {
    title: 'Build open science',
    description: 'Labora mentors are part of a movement toward more transparent, accessible science. Your participation helps make that real.',
    icon: '🔬',
  },
];

const MentorCard = ({ mentor }) => {
  const [requested, setRequested] = useState(false);

  return (
    <div className={styles.mentorCard}>
      <div className={styles.mentorTop}>
        <div
          className={styles.mentorAvatar}
          style={{ background: `${mentor.color}18`, color: mentor.color }}
        >
          {mentor.initials}
        </div>
        <div className={styles.mentorMeta}>
          <div className={styles.mentorName}>{mentor.name}</div>
          <div className={styles.mentorTitle}>{mentor.title}</div>
          <span
            className={styles.fieldBadge}
            style={{ color: mentor.color, background: `${mentor.color}15` }}
          >
            {mentor.field}
          </span>
        </div>
      </div>

      <p className={styles.mentorBio}>{mentor.bio}</p>

      <div className={styles.topics}>
        {mentor.topics.map(t => (
          <span key={t} className={styles.topic}>{t}</span>
        ))}
      </div>

      <button
        className={`${styles.requestBtn} ${!mentor.available ? styles.requestBtnUnavailable : ''} ${requested ? styles.requestBtnSent : ''}`}
        onClick={() => mentor.available && setRequested(true)}
        disabled={!mentor.available || requested}
      >
        {requested
          ? '✓ Request sent!'
          : mentor.available
            ? 'Request Mentorship'
            : 'Currently unavailable'}
      </button>
    </div>
  );
};

const Mentorship = () => (
  <main className={styles.main}>

    {/* ══════════════════════════════════════
        HERO
    ══════════════════════════════════════ */}
    <section className={styles.hero}>
      <div className={styles.heroGlow} aria-hidden="true" />
      <div className={styles.container}>
        <AnimatedSection className={styles.heroContent}>
          <span className={styles.eyebrow}>Labora Mentorship</span>
          <h1 className={styles.heroTitle}>
            Every scientist{' '}
            <span className={styles.titleAccent}>needs a guide.</span>
          </h1>
          <p className={styles.heroSub}>
            Labora Mentorship connects young researchers with experienced scientists who have navigated the same questions, frustrations, and breakthroughs you're facing. One conversation can change the direction of your work.
          </p>
          <div className={styles.heroCtas}>
            <a href="#mentors" className={styles.ctaPrimary}>
              Find a mentor
              <svg viewBox="0 0 20 20" fill="none">
                <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
            <a href="#become-mentor" className={styles.ctaSecondary}>
              Become a mentor
            </a>
          </div>

          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatVal}>40+</span>
              <span className={styles.heroStatLabel}>Active mentors</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatVal}>12</span>
              <span className={styles.heroStatLabel}>Science fields</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatVal}>180+</span>
              <span className={styles.heroStatLabel}>Connections made</span>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        MENTOR GRID
    ══════════════════════════════════════ */}
    <section className={styles.mentorsSection} id="mentors">
      <div className={styles.container}>
        <AnimatedSection className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>Meet the mentors</span>
          <h2 className={styles.sectionTitle}>Scientists who want to help</h2>
          <p className={styles.sectionSub}>
            These researchers have volunteered their time to support young scientists like you. Each one brings real experience and a genuine desire to give back.
          </p>
        </AnimatedSection>

        <div className={styles.mentorGrid}>
          {mentors.map((mentor, i) => (
            <AnimatedSection key={mentor.id} delay={i * 70}>
              <MentorCard mentor={mentor} />
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>

    {/* ══════════════════════════════════════
        WHY BECOME A MENTOR (for professionals)
    ══════════════════════════════════════ */}
    <section className={styles.whySection}>
      <div className={styles.container}>
        <AnimatedSection className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>For professionals</span>
          <h2 className={styles.sectionTitle}>Why become a Labora mentor?</h2>
          <p className={styles.sectionSub}>
            Labora mentors give an hour a month and change a young scientist's trajectory. Here's why thousands of researchers are joining.
          </p>
        </AnimatedSection>

        <div className={styles.whyGrid}>
          {whyMentor.map((item, i) => (
            <AnimatedSection key={item.title} delay={i * 80} className={styles.whyCard}>
              <div className={styles.whyEmoji}>{item.icon}</div>
              <h3 className={styles.whyTitle}>{item.title}</h3>
              <p className={styles.whyDesc}>{item.description}</p>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>

    {/* ══════════════════════════════════════
        BECOME A MENTOR CTA
    ══════════════════════════════════════ */}
    <section className={styles.becomeMentorSection} id="become-mentor">
      <div className={styles.ctaGlow} aria-hidden="true" />
      <div className={styles.container}>
        <AnimatedSection className={styles.becomeMentorCard}>
          <div className={styles.becomeMentorText}>
            <span className={styles.becomeMentorEyebrow}>Join as a mentor</span>
            <h2 className={styles.becomeMentorTitle}>
              Share what you know. Someone needs it right now.
            </h2>
            <p className={styles.becomeMentorSub}>
              You don't need to be famous or have decades of experience. You just need to have navigated something that a young scientist is about to face — and be willing to talk about it honestly.
            </p>
          </div>
          <div className={styles.becomeMentorActions}>
            <button className={styles.becomeMentorBtn} onClick={() => alert('Mentor application coming soon! We\'ll reach out to registered users.')}>
              Apply to be a mentor
              <svg viewBox="0 0 20 20" fill="none">
                <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <p className={styles.becomeMentorNote}>
              Takes about 5 minutes. We'll review your application within 3 days.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>

  </main>
);

export default Mentorship;
